import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListAllpayUsersQueryKey,
  useDeleteAllpayUser,
} from '@/api/trafikportalen';
import { BulkTravellerNamedList } from '@/components/Travellers/BulkTravellerNamedList';
import { BulkTravellerProgress } from '@/components/Travellers/BulkTravellerProgress';
import Button from '@/components/core/Button';
import type { BulkAllpayTraveller } from '@/helpers/bulkAllpayAssign';
import {
  getBulkAllpayCreditReleased,
  getBulkAllpayRemovePlan,
  type BulkAllpayRemoveSkipReason,
} from '@/helpers/bulkAllpayRemove';
import { getTravellerDisplayName } from '@/helpers/bulkTravellerOperation';
import { useBulkTravellerOperation } from '@/hooks/useBulkTravellerOperation';
import { IconTrash } from '@/icons';

interface BulkAllpayRemoveFormProps {
  travellers: BulkAllpayTraveller[];
  filteredDepartmentIds: string[];
  onOpenChange: (open: boolean) => void;
  onProcessingChange: (processing: boolean) => void;
  onComplete: (failedTravellerIds: string[]) => void;
}

export const BulkAllpayRemoveForm = ({
  travellers,
  filteredDepartmentIds,
  onOpenChange,
  onProcessingChange,
  onComplete,
}: BulkAllpayRemoveFormProps) => {
  const { formatMessage, locale } = useIntl();
  const queryClient = useQueryClient();
  const deleteAllpayUser = useDeleteAllpayUser();
  const {
    isProcessing,
    isComplete,
    processedCount,
    progressTotal,
    currentName,
    results,
    remainingTimeLabel,
    showRemainingTime,
    run,
  } = useBulkTravellerOperation({
    onProcessingChange,
    onComplete,
  });

  const anonymousName = formatMessage({ defaultMessage: 'Anonym resenär' });

  const skipReasonMessage = useCallback(
    (reason: BulkAllpayRemoveSkipReason) => {
      switch (reason) {
        case 'no-allpay':
          return formatMessage({
            defaultMessage: 'Har ej tjänsteresa i app',
          });
        case 'no-access':
          return formatMessage({
            defaultMessage: 'Du saknar skrivrättighet för avdelningen',
          });
        case 'ambiguous-department':
          return formatMessage({
            defaultMessage:
              'Har tjänsteresa i app för flera avdelningar i det aktuella filtret',
          });
      }
    },
    [formatMessage],
  );

  const plans = useMemo(
    () =>
      travellers.map((traveller) => ({
        traveller,
        plan: getBulkAllpayRemovePlan(traveller, filteredDepartmentIds),
      })),
    [filteredDepartmentIds, travellers],
  );

  const summary = useMemo(() => {
    return plans.reduce(
      (acc, { plan }) => {
        if (plan.action === 'remove') {
          acc.remove += 1;
        } else {
          acc.skip += 1;
        }
        return acc;
      },
      { remove: 0, skip: 0 },
    );
  }, [plans]);

  const actionablePlans = useMemo(
    () => plans.filter(({ plan }) => plan.action !== 'skip'),
    [plans],
  );

  const skippedTravellers = useMemo(
    () => plans.filter(({ plan }) => plan.action === 'skip'),
    [plans],
  );

  const hasAmbiguousDepartment = skippedTravellers.some(
    ({ plan }) =>
      plan.action === 'skip' && plan.reason === 'ambiguous-department',
  );

  const creditUsage = useMemo(
    () => getBulkAllpayCreditReleased(actionablePlans.map(({ plan }) => plan)),
    [actionablePlans],
  );

  const canConfirm = actionablePlans.length > 0 && !isProcessing;

  const handleConfirm = useCallback(() => {
    void run({
      items: actionablePlans,
      canStart: canConfirm,
      getItem: ({ traveller }) => ({
        id: traveller.id,
        name: getTravellerDisplayName(traveller, anonymousName),
      }),
      execute: async ({ plan }) => {
        if (plan.action === 'remove') {
          for (const user of plan.users) {
            await deleteAllpayUser.mutateAsync({ id: user.id });
          }
        }
      },
      afterAll: async () => {
        await queryClient.invalidateQueries({
          queryKey: getListAllpayUsersQueryKey(),
        });
      },
    });
  }, [
    actionablePlans,
    anonymousName,
    canConfirm,
    deleteAllpayUser,
    queryClient,
    run,
  ]);

  const handleClose = useCallback(() => {
    if (isProcessing) {
      return;
    }
    onOpenChange(false);
  }, [isProcessing, onOpenChange]);

  return (
    <div className="flex flex-col gap-5">
      {!isProcessing && !isComplete && (
        <>
          <p className="text-text-800">
            <FormattedMessage defaultMessage="Är du säker på att du vill ta bort tjänsteresa i app från de markerade resenärerna?" />
          </p>
          <div className="flex flex-col gap-1 text-text-800">
            {creditUsage > 0 && (
              <p>
                <FormattedMessage
                  defaultMessage="{amount} SEK av organisationens kredit frigörs"
                  values={{
                    amount: creditUsage.toLocaleString(locale),
                  }}
                />
              </p>
            )}
            <p>
              <FormattedMessage
                defaultMessage="{count, plural, one {# resenär vald} other {# resenärer valda}}"
                values={{ count: travellers.length }}
              />
            </p>
            {summary.remove > 0 && (
              <p>
                <FormattedMessage
                  defaultMessage="{count, plural, one {# resenär får tjänsteresa i app borttagen} other {# resenärer får tjänsteresa i app borttagen}}"
                  values={{ count: summary.remove }}
                />
              </p>
            )}
            {summary.skip > 0 && (
              <p>
                <FormattedMessage
                  defaultMessage="{count, plural, one {# resenär hoppas över} other {# resenärer hoppas över}}"
                  values={{ count: summary.skip }}
                />
              </p>
            )}
            {skippedTravellers.length > 0 && (
              <BulkTravellerNamedList
                className="mt-1"
                ariaLabel={formatMessage({
                  defaultMessage: 'Resenärer som hoppas över',
                })}
                items={skippedTravellers.map(({ traveller, plan }) => ({
                  id: traveller.id,
                  name: getTravellerDisplayName(traveller, anonymousName),
                  detail:
                    plan.action === 'skip'
                      ? skipReasonMessage(plan.reason)
                      : undefined,
                }))}
              />
            )}
            {hasAmbiguousDepartment && (
              <p className="mt-2 text-text-800">
                <FormattedMessage defaultMessage="Resenärer som har tjänsteresa i app för mer än en avdelning i det aktuella filtret hoppas över. Filtrera på en avdelning för att ta bort tjänsteresan från dem." />
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleClose}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              color="red"
              variant="filled"
              startIcon={<IconTrash aria-hidden="true" />}
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              <FormattedMessage defaultMessage="Radera" />
            </Button>
          </div>
        </>
      )}

      {(isProcessing || isComplete) && (
        <BulkTravellerProgress
          isProcessing={isProcessing}
          processedCount={processedCount}
          progressTotal={progressTotal}
          currentName={currentName}
          remainingTimeLabel={remainingTimeLabel}
          showRemainingTime={showRemainingTime}
          results={results}
          progressAriaLabel={formatMessage({
            defaultMessage: 'Förlopp för borttagning av tjänsteresa i app',
          })}
          stayOnPageMessage={
            <FormattedMessage defaultMessage="Lämna inte sidan. Borttagningen görs en resenär i taget i webbläsaren och avbryts om du navigerar bort." />
          }
          onClose={handleClose}
        />
      )}
    </div>
  );
};
